import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Media, SnakeToCamelObject } from '@browsing/api';
import { getBannerDesktopImage, getBannerMobileImage } from '@browsing/campaign';
import { useConfig } from '@browsing/config';

import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { APPDOMAIN_TO_TAB_ID_MAP } from '@client/config/constants';
import { isXsScreen } from '@client/helpers/screens';
import { CategoryTabIds } from '@shared/constants';

import { FunctionColor } from '@lux/core';
import { CategoryTab } from '@shared/types/mylounge';
import { getGlobalRetailEvents } from '@client/redux/modules/mylounge';
import { useNavigate } from 'react-router-dom';
import { Image } from './banner-slider.styles';
import BannerContent from './banner-content';
import { ANIMATION_WAIT } from './constants';
import { GRETabHeader, useGRE } from '../gre';

type ConfigAppDomainID = keyof typeof APPDOMAIN_TO_TAB_ID_MAP;

export interface BannerSliderProps {
    tabs: CategoryTab[];
    activeIndex: number;
    lastIndex: number;
    onBannerSwipe: (index: number) => void;
    tabImages: { [k: number]: boolean };
    onImageClick: () => void;
}

const BannerSlider: React.FC<BannerSliderProps> = ({
    tabs,
    activeIndex,
    lastIndex,
    onImageClick,
    tabImages,
    onBannerSwipe,
}) => {
    // States
    const [movement, setMovement] = useState({
        start: 0,
        current: 0,
        limit: 0,
    });
    const [movedIndex, setMovedIndex] = useState(0);
    const [animationStyles, setAnimationStyles] = useState<
        Record<number, { left: string; transition: string }>
    >({});

    // Selectors
    const deviceWidth = useSelector(getClientDeviceWidth);
    const globalRetailEvent = useSelector(getGlobalRetailEvents)?.[0];

    // Other Hooks
    const bannerRef = React.useRef(null);
    const timerRefId = React.useRef<any | null>(null);
    const config = useConfig();
    const {
        greTabData,
        shouldRenderGRETabHeader,
        shouldRenderGRECarousel,
        isGREEnabled,
        checkIfGRETab,
        isCyberWeek,
    } = useGRE();

    const navigate = useNavigate();

    const isMainTabActive = tabs[activeIndex].id === CategoryTabIds.ALL;

    const isMainTab = (tab: any) => tab.id === CategoryTabIds.ALL;

    const shouldLoadImage = (tabIndex: number) => {
        return tabIndex === activeIndex || tabImages[tabIndex];
    };

    const getBannerImage = (tab: CategoryTab) => {
        const isLastChanceTab =
            String(tab.id) ===
            String(APPDOMAIN_TO_TAB_ID_MAP[config.appdomainid as ConfigAppDomainID]);

        if (!isXsScreen(deviceWidth) || isLastChanceTab) {
            return getBannerDesktopImage(tab);
        }

        return getBannerMobileImage(tab);
    };

    const getBannerImageMedia = (tab: CategoryTab): SnakeToCamelObject<Media> => {
        if (isGREEnabled && globalRetailEvent && isMainTab(tab)) {
            if (globalRetailEvent.media?.url) {
                return {
                    ...globalRetailEvent.media,
                    altText: '',
                    url: globalRetailEvent.media.url,
                };
            }

            if (globalRetailEvent.image) {
                return {
                    mediaType: 'IMAGE',
                    altText: '',
                    url: globalRetailEvent.image,
                };
            }
        }

        return getBannerImage(tab);
    };

    const handleSlideMoveStart = (event: any) => {
        if (!event.touches || !event.touches.length) {
            return;
        }
        const touch = event.touches[0];
        const bannerElem = bannerRef.current;
        const { width } = bannerElem.getBoundingClientRect();

        const limit = width * 0.4;

        setMovement({
            start: touch.clientX,
            current: touch.clientX,
            limit,
        });
    };

    const resetMovement = () => {
        setMovement({
            start: 0,
            current: 0,
            limit: 0,
        });
        setAnimationStyles({});

        if (timerRefId.current) clearTimeout(timerRefId.current);

        if (movedIndex !== null) {
            timerRefId.current = setTimeout(() => {
                setMovedIndex(null);
            }, ANIMATION_WAIT);
        }
    };

    const getActiveLeftStyle = (movementPerc: number) => {
        if (!movementPerc) return {};

        return {
            id: activeIndex,
            left: -movementPerc,
        };
    };

    const getSlideToChangeTo = (percantage: number, currentIndex: number, max: number) => {
        const threshold = 0.3;

        if (percantage > threshold) {
            const nextSlide = currentIndex + 1;

            return nextSlide > max ? currentIndex : nextSlide;
        }

        if (percantage < threshold * -1) {
            const prevSlide = currentIndex - 1;

            return prevSlide < 0 ? currentIndex : prevSlide;
        }

        return null;
    };

    const updateMovedIndex = (other: { id: number; left: number }) => {
        if (!other.left || Math.abs(other.left) === 100) {
            return;
        }

        setMovedIndex(other.id);
    };

    const getOtherLeftStyle = (movementPerc: number) => {
        const tabIndex = movementPerc < 0 ? activeIndex - 1 : activeIndex + 1;
        const diff = tabIndex - activeIndex;
        const directionActive = diff > 0 ? movementPerc > 0 : movementPerc < 0;
        const leftValue =
            Math.sign(diff) * (100 - Math.abs(movementPerc) * Number(directionActive));

        return {
            id: tabIndex,
            left: leftValue,
        };
    };

    const updateAnimationStyles = (movementPerc: number) => {
        const active = getActiveLeftStyle(movementPerc);
        const other = getOtherLeftStyle(movementPerc);

        setAnimationStyles({
            [active.id]: { left: `${active.left}%`, transition: 'none' },
            [other.id]: { left: `${other.left}%`, transition: 'none' },
        });

        updateMovedIndex(other);
    };

    const handleSlideMove = (event: any) => {
        if (!event.touches || !event.touches.length) {
            return;
        }

        const touch = event.touches[0];
        const { start, limit } = movement;
        const current = touch.clientX;
        const movementPerc = Math.floor(((start - current) / deviceWidth) * 100);
        updateAnimationStyles(movementPerc);

        setMovement({
            start,
            current,
            limit,
        });
    };

    const handleSlideMoveEnd = (event: any) => {
        const movementPerc = (movement.start - movement.current) / movement.limit;

        const slideToChangeTo = getSlideToChangeTo(movementPerc, activeIndex, tabs.length - 1);
        resetMovement();

        if (slideToChangeTo === null) {
            return;
        }

        if (event.cancelable) {
            event.preventDefault();
        }

        onBannerSwipe(slideToChangeTo);
    };

    const handleBannerClick = () => {
        if (isGREEnabled && greTabData?.id && isMainTabActive) {
            navigate(`/event/${greTabData.id}${window.location.search}`);
        } else {
            onImageClick();
        }
    };

    useEffect(() => {
        return () => {
            if (timerRefId.current) {
                clearTimeout(timerRefId.current);
            }
        };
    }, []);

    const hasGRETabHeader = shouldRenderGRETabHeader && globalRetailEvent;

    return (
        <div data-testid="thb-wrapper" ref={bannerRef}>
            {tabs.map((tab, tabIndex) => {
                const isGRETab = checkIfGRETab(tab);
                const media = getBannerImageMedia(tab);

                return (
                    <Image
                        id={`thb-${tab?.thb?.id}`}
                        data-testid={`thb-${tab?.thb?.id}`}
                        data-position={tabIndex}
                        key={tab.id}
                        role="img"
                        aria-hidden={tabIndex !== activeIndex}
                        aria-label={media.altText}
                        style={
                            isGRETab && isGREEnabled
                                ? {
                                      backgroundColor: FunctionColor.function0,
                                      ...(animationStyles[tabIndex] || {}),
                                  }
                                : {
                                      backgroundImage: shouldLoadImage(tabIndex)
                                          ? `url(${media.url})`
                                          : '',
                                      ...(animationStyles[tabIndex] || {}),
                                  }
                        }
                        animated={
                            isGREEnabled && (isGRETab || lastIndex === tabs.length - 1)
                                ? false
                                : tabIndex === activeIndex ||
                                  tabIndex === lastIndex ||
                                  tabIndex === movedIndex
                        }
                        clickable={!!tab?.thb?.id || !!tab?.thb?.url}
                        active={tabIndex === activeIndex}
                        next={tabIndex > activeIndex}
                        previous={tabIndex < activeIndex}
                        lastChance={
                            String(tab.id) ===
                            String(APPDOMAIN_TO_TAB_ID_MAP[config.appdomainid as ConfigAppDomainID])
                        }
                        onTouchStart={handleSlideMoveStart}
                        onTouchMove={handleSlideMove}
                        onTouchEnd={handleSlideMoveEnd}
                        onClick={handleBannerClick}
                        onKeyPress={handleBannerClick}
                    >
                        {isGRETab && hasGRETabHeader && (
                            <GRETabHeader
                                isCyberWeek={isCyberWeek}
                                globalRetailEvent={globalRetailEvent}
                            />
                        )}
                        {!(isGRETab && shouldRenderGRETabHeader) && !shouldRenderGRECarousel && (
                            <BannerContent
                                active={tabIndex === activeIndex}
                                thb={tab.thb}
                                onButtonClick={onImageClick}
                            />
                        )}
                    </Image>
                );
            })}
        </div>
    );
};

export default BannerSlider;
